import React, { useState, useEffect, useContext } from 'react';
import {
  Typography, Table, Tag, Button, Space, Modal, Form, Input, Select, Checkbox, message, Divider,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../api';
import { formatTS } from '../utils/time';
import { UserContext } from '../App';
import CommonLinksConfig from '../components/CoachingCommonLinks';

function ConfigTable({ title, description, rows, loading, onAdd, onEdit, columns, isAdmin }) {
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Title level={5} style={{ margin: 0 }}>{title}</Typography.Title>
        {isAdmin && <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>Add</Button>}
      </div>
      {description && (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {description}
        </Typography.Paragraph>
      )}
      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns(onEdit)}
        loading={loading}
        size="small"
        pagination={false}
      />
    </>
  );
}

const statusCol = {
  title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
  render: (v) => <Tag color={v === 'active' ? 'green' : 'default'}>{v}</Tag>,
};
const createdCol = {
  title: 'Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 175,
  render: (v) => formatTS(v),
};

export default function CoachingConfigPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';
  // ── Areas ──────────────────────────────────────────────────────────────────
  const [areas, setAreas]         = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areaModal, setAreaModal] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [savingArea, setSavingArea]   = useState(false);
  const [areaForm] = Form.useForm();

  const loadAreas = async () => {
    setAreasLoading(true);
    try {
      const res = await api.get('/api/coaching/areas', { params: { all: '1' } });
      setAreas(res.data);
    } catch { message.error('Failed to load areas.'); }
    finally { setAreasLoading(false); }
  };

  useEffect(() => { loadAreas(); }, []);

  const openAddArea = () => {
    setEditingArea(null);
    areaForm.resetFields();
    areaForm.setFieldsValue({ status: 'active' });
    setAreaModal(true);
  };

  const openEditArea = (row) => {
    setEditingArea(row);
    areaForm.setFieldsValue({ name: row.Name, status: row.Status });
    setAreaModal(true);
  };

  const handleSaveArea = async () => {
    let values;
    try { values = await areaForm.validateFields(); }
    catch { return; }
    setSavingArea(true);
    try {
      if (editingArea) {
        await api.put(`/api/coaching/areas/${editingArea.ID}`, values);
        message.success('Area updated.');
      } else {
        await api.post('/api/coaching/areas', values);
        message.success('Area created.');
      }
      setAreaModal(false);
      loadAreas();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally { setSavingArea(false); }
  };

  // ── Request Types ──────────────────────────────────────────────────────────
  const [reqTypes, setReqTypes]         = useState([]);
  const [reqTypesLoading, setReqTypesLoading] = useState(false);
  const [reqTypeModal, setReqTypeModal] = useState(false);
  const [editingReqType, setEditingReqType] = useState(null);
  const [savingReqType, setSavingReqType]   = useState(false);
  const [reqTypeForm] = Form.useForm();

  const loadReqTypes = async () => {
    setReqTypesLoading(true);
    try {
      const res = await api.get('/api/coaching/request-types', { params: { all: '1' } });
      setReqTypes(res.data);
    } catch { message.error('Failed to load request types.'); }
    finally { setReqTypesLoading(false); }
  };

  useEffect(() => { loadReqTypes(); }, []);

  const openAddReqType = () => {
    setEditingReqType(null);
    reqTypeForm.resetFields();
    reqTypeForm.setFieldsValue({ status: 'active' });
    setReqTypeModal(true);
  };

  const openEditReqType = (row) => {
    setEditingReqType(row);
    reqTypeForm.setFieldsValue({ name: row.Name, status: row.Status, estimated_effort_hrs: row.EstimatedEffortHrs ?? '', is_recoaching: !!row.IsRecoaching });
    setReqTypeModal(true);
  };

  const handleSaveReqType = async () => {
    let values;
    try { values = await reqTypeForm.validateFields(); }
    catch { return; }
    setSavingReqType(true);
    try {
      if (editingReqType) {
        await api.put(`/api/coaching/request-types/${editingReqType.ID}`, values);
        message.success('Request type updated.');
      } else {
        await api.post('/api/coaching/request-types', values);
        message.success('Request type created.');
      }
      setReqTypeModal(false);
      loadReqTypes();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally { setSavingReqType(false); }
  };

  // ── Interaction Types ──────────────────────────────────────────────────────
  const [types, setTypes]           = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typeModal, setTypeModal]   = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [savingType, setSavingType]   = useState(false);
  const [typeForm] = Form.useForm();

  const loadTypes = async () => {
    setTypesLoading(true);
    try {
      const res = await api.get('/api/coaching/interaction-types', { params: { all: '1' } });
      setTypes(res.data);
    } catch { message.error('Failed to load interaction types.'); }
    finally { setTypesLoading(false); }
  };

  useEffect(() => { loadTypes(); }, []);

  const openAddType = () => {
    setEditingType(null);
    typeForm.resetFields();
    typeForm.setFieldsValue({ status: 'active' });
    setTypeModal(true);
  };

  const openEditType = (row) => {
    setEditingType(row);
    typeForm.setFieldsValue({ name: row.Name, status: row.Status });
    setTypeModal(true);
  };

  const handleSaveType = async () => {
    let values;
    try { values = await typeForm.validateFields(); }
    catch { return; }
    setSavingType(true);
    try {
      if (editingType) {
        await api.put(`/api/coaching/interaction-types/${editingType.ID}`, values);
        message.success('Type updated.');
      } else {
        await api.post('/api/coaching/interaction-types', values);
        message.success('Type created.');
      }
      setTypeModal(false);
      loadTypes();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally { setSavingType(false); }
  };

  const nameStatusColumns = (onEdit) => [
    { title: 'Name', dataIndex: 'Name', key: 'Name' },
    statusCol,
    createdCol,
    ...(isAdmin ? [{
      title: '', key: 'actions', width: 80,
      render: (_, r) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)}>Edit</Button>
      ),
    }] : []),
  ];

  const nameStatusForm = (form) => (
    <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
      <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
        <Input />
      </Form.Item>
      <Form.Item label="Status" name="status">
        <Select options={[
          { label: 'Active',   value: 'active' },
          { label: 'Inactive', value: 'inactive' },
        ]} />
      </Form.Item>
    </Form>
  );

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 24 }}>Coaching Config</Typography.Title>

      <ConfigTable
        title="Areas"
        description="Areas define the focus of a coaching session. Only active areas appear when creating a new session."
        rows={areas}
        loading={areasLoading}
        onAdd={openAddArea}
        onEdit={openEditArea}
        columns={nameStatusColumns}
        isAdmin={isAdmin}
      />

      <Divider />

      <ConfigTable
        title="Interaction Types"
        description="Interaction types categorize linked calls (e.g. Good Example, Bad Example). Only active types appear in the dropdown when adding an interaction."
        rows={types}
        loading={typesLoading}
        onAdd={openAddType}
        onEdit={openEditType}
        columns={nameStatusColumns}
        isAdmin={isAdmin}
      />

      {/* Areas modal */}
      <Modal maskClosable={false}
        title={editingArea ? 'Edit Area' : 'Add Area'}
        open={areaModal}
        onOk={handleSaveArea}
        onCancel={() => setAreaModal(false)}
        confirmLoading={savingArea}
        okText="Save"
        destroyOnClose
      >
        {nameStatusForm(areaForm)}
      </Modal>

      {/* Interaction Types modal */}
      <Modal maskClosable={false}
        title={editingType ? 'Edit Interaction Type' : 'Add Interaction Type'}
        open={typeModal}
        onOk={handleSaveType}
        onCancel={() => setTypeModal(false)}
        confirmLoading={savingType}
        okText="Save"
        destroyOnClose
      >
        {nameStatusForm(typeForm)}
      </Modal>

      <Divider />

      <ConfigTable
        title="Request Types"
        description="Request types categorize the reason a coaching session was requested. Only active types appear when creating a new session."
        rows={reqTypes}
        loading={reqTypesLoading}
        onAdd={openAddReqType}
        onEdit={openEditReqType}
        isAdmin={isAdmin}
        columns={(onEdit) => [
          { title: 'Name', dataIndex: 'Name', key: 'Name' },
          {
            title: 'Est. Effort (hrs)', dataIndex: 'EstimatedEffortHrs', key: 'EstimatedEffortHrs', width: 150,
            render: (v) => v != null ? `${v} hr${v === 1 ? '' : 's'}` : <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: 'Counts as Recoaching', dataIndex: 'IsRecoaching', key: 'IsRecoaching', width: 160, align: 'center',
            render: (v) => v ? <Tag color="blue">Yes</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
          },
          statusCol,
          createdCol,
          ...(isAdmin ? [{
            title: '', key: 'actions', width: 80,
            render: (_, r) => (
              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(r)}>Edit</Button>
            ),
          }] : []),
        ]}
      />

      <Divider />

      <CommonLinksConfig isAdmin={isAdmin} />

      {/* Request Types modal */}
      <Modal maskClosable={false}
        title={editingReqType ? 'Edit Request Type' : 'Add Request Type'}
        open={reqTypeModal}
        onOk={handleSaveReqType}
        onCancel={() => setReqTypeModal(false)}
        confirmLoading={savingReqType}
        okText="Save"
        destroyOnClose
      >
        <Form form={reqTypeForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Estimated Effort (hrs)" name="estimated_effort_hrs"
            help="Enter hours as a decimal, e.g. 0.25 = 15 min, 0.5 = 30 min, 1 = 1 hr">
            <Input type="number" step="0.25" min="0" placeholder="e.g. 0.5" />
          </Form.Item>
          <Form.Item label="Status" name="status">
            <Select options={[
              { label: 'Active',   value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ]} />
          </Form.Item>
          <Form.Item name="is_recoaching" valuePropName="checked">
            <Checkbox>Counts as Recoaching</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
